import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type LeadPredictionResult } from '@whinerp/client';
import { formatCount, formatDecimal, formatPercent } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The predictive score beside the rule-based one (F-077), with the factors
 * behind it and how well the model tests - or, before there is enough history,
 * how many more closed leads it needs.
 */
export function LeadPredictionPanel({ leadId, ruleScore }: { leadId: string; ruleScore: number | null }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const enabled = useEntitlement('F-077');
  const prediction = useQuery<LeadPredictionResult, ApiError>({
    queryKey: ['lead-prediction', leadId],
    queryFn: () => api.leadPrediction({ path: { leadId } }),
    enabled: enabled.enabled,
  });
  if (!enabled.enabled || !prediction.data) return null;
  const { probability, factors, status } = prediction.data;
  return (
    <Panel data-region="lead-prediction">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('predict.title')}</SectionLabel>
        {probability === null ? (
          <p data-prediction-waiting className="text-meta text-ink-meta">
            {t('predict.waiting', { values: { needed: formatCount(status.needed, format), closed: formatCount(status.closed, format) } })}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span data-prediction-score className="rounded-chip bg-bronze-tint px-2 text-dense text-ink">
                {t('predict.chance', { values: { percent: formatPercent(probability, format) } })}
              </span>
              {ruleScore !== null ? <span className="text-meta text-ink-meta">{t('predict.ruleScore', { values: { score: formatCount(ruleScore, format) } })}</span> : null}
            </div>
            <ul data-prediction-factors className="flex flex-col gap-1">
              {factors.map((factor) => (
                <li key={factor.label} className={factor.weight >= 0 ? 'text-meta text-success-text' : 'text-meta text-danger-text'}>
                  {factor.weight >= 0 ? '▲' : '▼'} {factor.label} ({formatDecimal(String(factor.weight), format)})
                </li>
              ))}
            </ul>
            <p className="text-meta text-ink-meta">
              {t('predict.trust', {
                values: {
                  closed: formatCount(status.closed, format),
                  auc: status.auc === null ? '-' : formatPercent(status.auc, format),
                },
              })}
            </p>
          </>
        )}
      </div>
    </Panel>
  );
}
